/**
 * "Load sample data" (onboarding and Account → Workspace): the prototype's sample workspace — its
 * sources, topics, the Research tag, filters and nine messages in the four folders — created through the
 * PUBLIC Engine API, so plan limits apply as they would to anything the user creates.
 *
 * Within a Free plan's limits the extra sources, topics and filters are skipped (and reported); the
 * messages of a skipped source are delivered through a sample source that exists, so all four folders
 * still fill. Sample sources only receive pushed messages: they are paused afterwards so the workspace
 * never tries to pull from the sample addresses. Nothing is scored or moved by a rule: each message is
 * placed in its sample folder directly.
 */

import { FIXTURE_FILTERS, FIXTURE_MESSAGES, FIXTURE_SOURCES, FIXTURE_TAG_RESEARCH, FIXTURE_TOPICS, type FixtureSourceKey } from '@digester/core/testkit/fixtures.js';
import { DigesterError, messageId, toDigesterError, type Engine, type FolderId, type MessageId, type SourceConfig, type SourceId, type TagId, type TopicId } from '@digester/core';
import tramImage from './sample/tram.jpg';
import workspaceImage from './sample/workspace.jpg';

const SAMPLE_IMAGES: Readonly<Record<string, string>> = {
  'https://digester.example/assets/tram.jpg': tramImage,
  'https://digester.example/assets/workspace.jpg': workspaceImage,
};

export interface SampleReport {
  sources: number;
  topics: number;
  filters: number;
  messages: number;
  /** Sample records the plan did not allow (or that already existed). */
  skipped: string[];
}

function configOf(kind: string, locator: string): SourceConfig {
  if (kind === 'email') return { kind: 'email', host: 'mail.example', port: 993, security: 'tls', user: 'reader', mailbox: 'Newsletters' };
  if (kind === 'telegram') return { kind: 'telegram', mode: 'public', username: locator.split(':').pop() ?? 'urban_notes_sample', peerType: 'channel' };
  return { kind: 'rss', feedUrl: locator };
}

/** An absolute URL for a bundled sample image (the engine stores absolute URLs only). */
function imageUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  const local = SAMPLE_IMAGES[url];
  if (!local) return undefined;
  return new URL(local, location.href).href;
}

async function tryCreate<T>(label: string, skipped: string[], run: () => Promise<T>): Promise<T | null> {
  try {
    return await run();
  } catch (error) {
    const e = toDigesterError(error);
    if (e.code === 'plan_limit' || e.code === 'not_entitled' || e.code === 'conflict') {
      skipped.push(label);
      return null;
    }
    throw e;
  }
}

export async function loadSampleData(engine: Engine): Promise<SampleReport> {
  const skipped: string[] = [];
  const report: SampleReport = { sources: 0, topics: 0, filters: 0, messages: 0, skipped };

  // Sources first: the sample messages need somewhere to come from.
  const created = new Map<FixtureSourceKey, SourceId>();
  for (const s of FIXTURE_SOURCES) {
    const source = await tryCreate(`Source “${s.name}”`, skipped, () =>
      engine.sources.create({
        resolution: { kind: s.kind, locator: s.locator, name: s.name, config: configOf(s.kind, s.locator), requiredSecrets: [] },
        name: s.name,
        pullMinutes: s.pullMinutes,
        format: s.format,
        keepImages: s.keepImages,
        keepVideos: s.keepVideos,
        description: s.description,
      }),
    );
    if (source) {
      created.set(s.key, source.id);
      report.sources++;
    }
  }
  const fallback = [...created.values()][0];
  if (fallback === undefined) throw new DigesterError('plan_limit', 'No sample source could be created on this plan');

  const topics = new Map<string, TopicId>();
  for (const t of FIXTURE_TOPICS) {
    const topic = await tryCreate(`Topic “${t.name}”`, skipped, () => engine.topics.create({ name: t.name, hint: '', icon: t.icon, color: t.color, enabled: true }));
    if (topic) {
      topics.set(t.key, topic.id);
      report.topics++;
    }
  }
  const research = await tryCreate(`Tag “${FIXTURE_TAG_RESEARCH.name}”`, skipped, () => engine.tags.create({ name: FIXTURE_TAG_RESEARCH.name, icon: 'tag' }));

  // Messages: pushed through their source (or a sample source that exists), then placed and marked.
  const ids: MessageId[] = [];
  for (const m of FIXTURE_MESSAGES) {
    const sourceId = created.get(m.source) ?? fallback;
    const raw = { ...m.raw };
    const image = imageUrl(raw.imageUrl);
    if (image) raw.imageUrl = image;
    else delete raw.imageUrl;
    const result = await engine.sources.push(sourceId, [raw]);
    if (result.inserted === 0 && result.duplicates === 0) {
      skipped.push(`Message “${m.raw.title ?? m.key}”`);
      continue;
    }
    const id = messageId(sourceId, m.raw.externalId);
    ids.push(id);
    const detail = await engine.messages.get(id);
    if (detail.message.placement.folderId !== m.prototype.folder) await engine.messages.move([id], m.prototype.folder as FolderId);
    const topicIds = m.prototype.topics.map((t) => topics.get(t.topic)).filter((x): x is TopicId => x !== undefined);
    if (topicIds.length) await engine.messages.setTopics(id, topicIds);
    if (research && m.prototype.tags.includes(FIXTURE_TAG_RESEARCH.name)) await engine.messages.setTags(id, [research.id as TagId]);
    await engine.messages.setRead([id], m.prototype.read);
    report.messages++;
  }

  // Filters last, so they describe the sample without re-placing the messages above.
  for (const f of FIXTURE_FILTERS) {
    const actions = f.input.actions.map((a) => (a.type === 'add-tag' && research ? { ...a, tagId: research.id as TagId } : a));
    const rule = await tryCreate(`Filter “${f.input.name}”`, skipped, () => engine.rules.create({ ...f.input, actions }));
    if (rule) report.filters++;
  }

  // The sample addresses do not exist: keep the sources for their history, but never pull them.
  for (const id of created.values()) {
    await engine.sources.update(id, { enabled: false });
  }
  console.log(`[sample] loaded ${report.messages} messages, ${report.sources} sources, ${report.topics} topics, ${report.filters} filters; skipped ${skipped.length}`);
  return report;
}
